import { PropsWithChildren } from 'react';
import { useAsync } from 'react-use';
import { asyncWithLDProvider, ProviderConfig, useLDClientError } from 'launchdarkly-react-client-sdk';

import { LoadingSpinner, Result, Status } from '@oaknorth/component-core-v2';
import { T } from '@oaknorth/util-intl';
import { useAccessToken, useUserInfo } from '@oaknorth/util-user';

import { MockFeatureFlagsProvider } from './mock-flags';
import { useLaunchDarklyConfig } from './useLaunchDarklyConfig';

const LD_DEFAULT_REACT_OPTIONS: Pick<ProviderConfig, 'reactOptions'> = {
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
};

const LD_DEFAULT_OPTIONS: ProviderConfig['options'] = {
  baseUrl: '/api/feature-flags',
  eventsUrl: '/api/feature-flags',

  // we don't want the page to self-update while the user is on it
  streaming: false,

  // diagnostics requires storing data in localStorage
  // This is launchdarkly's analytics, no functionality depends on this
  diagnosticOptOut: true,
};

const FeatureFlagsError = () => (
  <Result status={Status.ERROR} title={T`featureFlags.error.title`} text={T`featureFlags.error.description`} />
);

function FeatureFlagsErrorHandler({ children }: PropsWithChildren<{}>) {
  const ldError = useLDClientError();

  if (ldError) {
    return <FeatureFlagsError />;
  }

  return <>{children}</>;
}

function CustomFeatureFlagsProvider({ children }: PropsWithChildren<{}>) {
  const userInfo = useUserInfo();

  const config = useLaunchDarklyConfig();
  const accessToken = useAccessToken();

  const provider = useAsync(async () => {
    if (!userInfo || !config.data?.launchDarklyClientSideId) {
      return null;
    }

    return await asyncWithLDProvider({
      ...LD_DEFAULT_REACT_OPTIONS,
      clientSideID: config.data.launchDarklyClientSideId,
      options: {
        ...LD_DEFAULT_OPTIONS,
        hash: config.data.contextHash,
        requestHeaderTransform: (headers) => {
          // eslint-disable-next-line @oaknorth/ui/no-template-literals -- auth header is not user facing
          return accessToken ? { ...headers, authorization: `Bearer ${accessToken}` } : headers;
        },
      },
      context: {
        // eslint-disable-next-line i18next/no-literal-string -- kind prop is for LD
        kind: 'user',
        key: userInfo.userId,
        tenantId: userInfo.tenantId,
        isOaknorth: userInfo.isONUser,
      },
    });
  }, [userInfo, config.data]);

  if (config.isError) {
    return <FeatureFlagsError />;
  }

  if (provider.loading || !provider.value || config.isPending) {
    return <LoadingSpinner />;
  }

  return (
    <provider.value>
      <FeatureFlagsErrorHandler>{children}</FeatureFlagsErrorHandler>
    </provider.value>
  );
}

export const FeatureFlagsProvider =
  process.env.NODE_ENV === 'development' && !('Cypress' in window)
    ? MockFeatureFlagsProvider
    : CustomFeatureFlagsProvider;
